import React from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {selectSnappingState, viewActions} from "../view/viewSlice";
import IconWithTooltip from "../../../../../../components/ui/IconWithTooltip";
import IconSnapAngle from "../../icons/selectableEditorHeaderIcons/IconSnapAngle";
import {AngleSnappingMode} from "../../../../../base-konva/types";
import {Popper} from "../../../../../../components/popper";
import {MinMaxValidatedNumberInput} from "../../components/MinMaxValidatedNumberInput";
import "./editorHeaderAngleSnappingDropdown.scss";

type AngleSnappingDropdownProps = {
	disabled?: boolean
}

export function EditorHeaderAngleSnappingDropdown({disabled}: AngleSnappingDropdownProps) {
	const {t} = useTranslation();
	const dispatch = useDispatch();
	const snapping = useSelector(selectSnappingState);

	const button = (
		<IconWithTooltip
			icon={<IconSnapAngle disabled={disabled} active={snapping.angle} onClick={() => {}}/>}
			tooltipText={t("editor.tooltip.snapAngle")}
		/>
	);

	function handleModeSelect(mode: AngleSnappingMode) {
		return () => {
			// Turn off point snapping when enabling angle snapping (mutual exclusivity)
			if (mode !== 'off' && snapping.point) {
				dispatch(viewActions.togglePointSnapping());
			}
			dispatch(viewActions.setAngleSnappingMode(mode));
		};
	}

	function handleCustomAngleChange(value: number) {
		dispatch(viewActions.setCustomAngle(value));
	}

	const modeOptions: {mode: AngleSnappingMode; label: string}[] = [
		{mode: 'off', label: t("editor.angleSnapping.off")},
		{mode: '15', label: "15°"},
		{mode: '45', label: "45°"},
		{mode: '90', label: "90°"},
		{mode: 'custom', label: t("editor.angleSnapping.custom")},
	];

	return (
		<Popper target={button} disabled={disabled}>
			<div className="editor-header-angle-snapping-dropdown">
				{modeOptions.map(({mode, label}) => (
					<div
						key={mode}
						className={`editor-header-angle-snapping-dropdown_item ${snapping.angleMode === mode ? 'active' : ''}`}
						onClick={handleModeSelect(mode)}
						onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleModeSelect(mode)(); } }}
						role="menuitem"
						tabIndex={0}
					>
						<span className="editor-header-angle-snapping-dropdown_item-label">{label}</span>
						{snapping.angleMode === mode && <CheckIcon/>}
					</div>
				))}
				{snapping.angleMode === 'custom' && (
					<div className="editor-header-angle-snapping-dropdown_custom-input">
						<MinMaxValidatedNumberInput
							min={1}
							max={90}
							value={snapping.customAngle}
							onChange={handleCustomAngleChange}
						/>
						<span className="editor-header-angle-snapping-dropdown_custom-input-suffix">°</span>
					</div>
				)}
			</div>
		</Popper>
	);
}

function CheckIcon() {
	return (
		<svg width={16} height={16} viewBox="0 0 24 24" fill="#707070">
			<path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
		</svg>
	);
}
